import arxivTopics from './topics.json'
import biorxivTopics from './biorxivTopics.json'
import psyarxivTopics from './psyarxivTopics.json'
import {
  FaAtom, FaGlobe, FaFlask, FaLaptopCode, FaDna, FaPlug, FaChartLine,
  FaCalculator, FaChartBar, FaBolt, FaProjectDiagram, FaRadiation,
  FaMicroscope, FaWaveSquare, FaSuperscript, FaBrain, FaLeaf, FaVirus,
  FaHeartbeat, FaPaw, FaSeedling, FaVial, FaCogs, FaUsers, FaBookOpen,
  FaUserMd, FaChild, FaHandshake, FaChalkboardTeacher, FaComments
} from 'react-icons/fa';

// ---------------------------------------------------------------------------
// Source detection
// ---------------------------------------------------------------------------

/**
 * Determine which paper source we are serving.
 *
 * Production: detected from the hostname (bio.paperdigest.xyz → biorxiv).
 * Development: read from the REACT_APP_PAPER_SOURCE env var so you can
 *   run `npm run dev:biorxiv` on localhost without subdomain tricks.
 */
function detectSource() {
  // Env var override (set at build/dev time via env-cmd)
  const envSource = process.env.REACT_APP_PAPER_SOURCE;
  if (envSource && ['arxiv', 'biorxiv', 'psyarxiv'].includes(envSource)) {
    return envSource;
  }

  // Runtime hostname detection (production + staging)
  // Handles: bio.paperdigest.xyz, dev.bio.paperdigest.xyz, etc.
  if (typeof window === 'undefined') return 'arxiv';
  const host = window.location.hostname;
  if (host.match(/(?:^|\.)bio\./)) return 'biorxiv';
  if (host.match(/(?:^|\.)psy\./)) return 'psyarxiv';
  return 'arxiv';
}

export const PAPER_SOURCE = detectSource();

export const SOURCE_NAMES = {
  arxiv: 'arXiv',
  biorxiv: 'bioRxiv',
  psyarxiv: 'psyArXiv',
};

export const SOURCE_NAME = SOURCE_NAMES[PAPER_SOURCE] || 'arXiv';

// ---------------------------------------------------------------------------
// Source-specific topics
// ---------------------------------------------------------------------------

const TOPICS_BY_SOURCE = {
  arxiv: arxivTopics,
  biorxiv: biorxivTopics,
  psyarxiv: psyarxivTopics,
};

export const TOPICS_DATA = TOPICS_BY_SOURCE[PAPER_SOURCE] || arxivTopics;

// ---------------------------------------------------------------------------
// Source-specific icons
// ---------------------------------------------------------------------------

const ARXIV_ICONS = {
  'Astrophysics': FaGlobe,
  'Condensed Matter Physics': FaFlask,
  'Quantum Physics': FaAtom,
  'Computer Science': FaLaptopCode,
  'Quantitative Biology': FaDna,
  'Electrical Engineering and Systems Science': FaPlug,
  'Economics and Finance': FaChartLine,
  'Mathematics': FaCalculator,
  'Statistics': FaChartBar,
  'High Energy Physics': FaBolt,
  'General Relativity': FaProjectDiagram,
  'Nuclear Physics': FaRadiation,
  'Physics': FaMicroscope,
  'Nonlinear Sciences': FaWaveSquare,
  'Mathematical Physics': FaSuperscript,
};

const BIORXIV_ICONS = {
  'Animal Behavior and Cognition': FaPaw,
  'Biochemistry': FaFlask,
  'Bioengineering': FaCogs,
  'Bioinformatics': FaLaptopCode,
  'Biophysics': FaAtom,
  'Cancer Biology': FaVirus,
  'Cell Biology': FaMicroscope,
  'Clinical Trials': FaUserMd,
  'Developmental Biology': FaChild,
  'Ecology': FaLeaf,
  'Epidemiology': FaChartBar,
  'Evolutionary Biology': FaDna,
  'Genetics': FaDna,
  'Genomics': FaDna,
  'Immunology': FaVirus,
  'Microbiology': FaVirus,
  'Molecular Biology': FaVial,
  'Neuroscience': FaBrain,
  'Paleontology': FaGlobe,
  'Pathology': FaMicroscope,
  'Pharmacology and Toxicology': FaVial,
  'Physiology': FaHeartbeat,
  'Plant Biology': FaSeedling,
  'Scientific Communication and Education': FaBookOpen,
  'Synthetic Biology': FaCogs,
  'Systems Biology': FaProjectDiagram,
  'Zoology': FaPaw,
};

const PSYARXIV_ICONS = {
  'Social and Behavioral Sciences': FaUsers,
  'Clinical Psychology': FaUserMd,
  'Cognitive Psychology': FaBrain,
  'Developmental Psychology': FaChild,
  'Social Psychology': FaHandshake,
  'Quantitative Psychology': FaChartBar,
  'Neuroscience': FaBrain,
  'Health Psychology': FaHeartbeat,
  'Industrial and Organizational Psychology': FaCogs,
  'Personality and Social Contexts': FaUsers,
  'Psychiatry and Psychology': FaUserMd,
  'Applied Behavior Analysis': FaChartLine,
  'Biological Psychology': FaDna,
  'Comparative Psychology': FaPaw,
  'Counseling Psychology': FaComments,
  'Educational Psychology': FaChalkboardTeacher,
  'Experimental Analysis of Behavior': FaFlask,
  'Geropsychology': FaUsers,
  'Psychology of Movement': FaHeartbeat,
  'School Psychology': FaChalkboardTeacher,
  'Other Psychology': FaBrain,
};

const ICONS_BY_SOURCE = {
  arxiv: ARXIV_ICONS,
  biorxiv: BIORXIV_ICONS,
  psyarxiv: PSYARXIV_ICONS,
};

export const TOPICS_ICONS = ICONS_BY_SOURCE[PAPER_SOURCE] || ARXIV_ICONS;
